import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "d-flex" }
const _hoisted_2 = { class: "d-flex flex-wrap flex-stack mb-6" }
const _hoisted_3 = { class: "fw-bold my-2 d-flex flex-column" }
const _hoisted_4 = { class: "d-flex justify-content-between" }
const _hoisted_5 = { class: "me-4" }
const _hoisted_6 = { class: "ms-1" }
const _hoisted_7 = { class: "fw-bold" }
const _hoisted_8 = { class: "fw-bold" }
const _hoisted_9 = {
  key: 0,
  class: "w-100 min-h-225px d-flex justify-content-center align-items-center"
}
const _hoisted_10 = {
  key: 0,
  style: {"flex":"1.5","height":"625px","overflow-y":"auto","padding":"20px"}
}

import { computed, onMounted, provide, ref, watch } from "vue";
import {
  ArrowLeftBold,
  ArrowRightBold,
  DocumentAdd,
} from "@element-plus/icons-vue";
import {
  VerificationDocumentTypes,
  VerificationStatusTypes,
  VerificationTypes,
} from "@/core/types/VerificationInfos";
import UserPersonalInfos from "@/projects/tenant/modules/users/components/userDocument/UserPersonalInfos.vue";
import DocumentFileUploadModal from "@/projects/tenant/modules/users/components/modal/DocumentFileUploadModal.vue";
import FinalizedAuKycModal from "@/projects/tenant/modules/users/components/modal/FinalizedAuKycModal.vue";
import FinalizedKycModal from "@/projects/tenant/modules/users/components/modal/FinalizedKycModal.vue";
import UserService from "@/projects/tenant/modules/users/services/UserService";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";
import HistoricalDocuments from "./HistoricalDocuments.vue";
import KycDocuments from "./KycDocuments.vue";
import OtherDocuments from "./OtherDocuments.vue";
import VerificationDocuments from "./VerificationDocuments.vue";
import {
  CurrentVerificationDocuments,
  userDocumentsInjectionKeys,
} from "./UserDocumentsInjectionKeys";

const APPROVED_DOCUMENT_STATUS = 2;


export default /*#__PURE__*/_defineComponent({
  __name: 'UserDocumentsV2',
  props: {
    verifyOperation: { type: Boolean },
    height: {},
    partyId: {}
  },
  emits: ["updateVerificationDetails"],
  setup(__props: any, { emit: emits }) {

const props = __props;





const isShowUserInfos = ref(false);
const isLoading = ref(false);
const kycLoading = ref(false);
const verificationDetails = ref<any>(null);
const finalizeKyc = ref<any>({});
const mediaList = ref<Array<any>>([]);
const historicalDocuments = ref<Array<any>>([]);
const otherDocuments = ref<Array<any>>([]);
const verificationDocuments = ref<CurrentVerificationDocuments>({
  idFront: null,
  idBack: null,
  address: null,
  selfie: null,
});
const needSelfie = ref(false);
const effectivePartyId = computed(() => props.partyId);
const canVerifyDocument = computed(() => props.verifyOperation === true);

const openDocumentFileUploadModal = () => {
  if (!verificationDetails.value?.id) return;
  documentFileUploadModal.value?.show(verificationDetails.value.id);
};

const documentFileUploadModal =
  ref<InstanceType<typeof DocumentFileUploadModal>>();
const finalizedKycModal = ref<InstanceType<typeof FinalizedKycModal>>();
const finalizedAuKycModal = ref<InstanceType<typeof FinalizedAuKycModal>>();

const verificationDocumentTypes = [
  VerificationDocumentTypes.IdFront,
  VerificationDocumentTypes.IdBack,
  VerificationDocumentTypes.Address,
  VerificationDocumentTypes.Selfie,
];

const resetDocuments = () => {
  verificationDocuments.value = {
    idFront: null,
    idBack: null,
    address: null,
    selfie: null,
  };
  needSelfie.value = false;
  historicalDocuments.value = [];
  otherDocuments.value = [];
};

const resetKyc = () => {
  finalizeKyc.value = {};
};

const getDocumentType = (doc: any) => doc?.documentType ?? doc?.type;

const getDocumentId = (doc: any) => Number(doc?.id ?? 0);

const getDocumentKey = (doc: any) => `${doc?.guid ?? ""}:${doc?.id ?? ""}`;

const isVerificationDocumentType = (documentType: any) =>
  verificationDocumentTypes.includes(documentType);

const mergeDocumentInfo = (verificationDoc: any, media?: any) => ({
  ...media,
  ...verificationDoc,
  contentType: media?.contentType ?? verificationDoc?.contentType,
  createdOn: media?.createdOn ?? verificationDoc?.createdOn,
  documentType: verificationDoc?.documentType ?? media?.documentType,
  fileName: media?.fileName ?? verificationDoc?.fileName,
  rejectedReason: verificationDoc?.rejectedReason ?? media?.rejectedReason,
  status: verificationDoc?.status ?? media?.status,
  type: media?.type ?? verificationDoc?.type,
  url: media?.url ?? verificationDoc?.url,
});

const isApprovedDocument = (doc: any) =>
  Number(doc?.status) === APPROVED_DOCUMENT_STATUS;

const pickLatestDocument = (documents: any[]) =>
  documents.reduce(
    (latest, doc) =>
      !latest || getDocumentId(doc) > getDocumentId(latest) ? doc : latest,
    null as any
  );

// 优先选已审批(approved)的文档；若没有已审批的则选最新上传的
const pickCurrentDocument = (documents: any[]) => {
  if (!documents.length) return null;
  const approvedDocuments = documents.filter(isApprovedDocument);
  return pickLatestDocument(
    approvedDocuments.length ? approvedDocuments : documents
  );
};

const buildDocuments = () => {
  resetDocuments();

  const verificationDocs = verificationDetails.value?.document ?? [];
  const mediaByGuid = new Map(
    mediaList.value.map((media) => [media.guid, media])
  );
  const verificationDocGuids = new Set(
    verificationDocs.map((doc) => doc.guid).filter(Boolean)
  );
  const mergedVerificationDocs = verificationDocs.map((doc) =>
    mergeDocumentInfo(doc, mediaByGuid.get(doc.guid))
  );
  const mediaWithoutVerificationDoc = mediaList.value.filter(
    (media) => !verificationDocGuids.has(media.guid)
  );
  const documents = [...mergedVerificationDocs, ...mediaWithoutVerificationDoc];

  // 按文档类型分组(仅 verification 类型: id_front/id_back/address/selfie)
  const documentsByType = {} as Record<string, any[]>;
  documents.forEach((doc) => {
    const documentType = getDocumentType(doc);
    if (!isVerificationDocumentType(documentType)) return;
    (documentsByType[documentType] ??= []).push(doc);
  });

  verificationDocuments.value = {
    idFront: pickCurrentDocument(
      documentsByType[VerificationDocumentTypes.IdFront] ?? []
    ),
    idBack: pickCurrentDocument(
      documentsByType[VerificationDocumentTypes.IdBack] ?? []
    ),
    address: pickCurrentDocument(
      documentsByType[VerificationDocumentTypes.Address] ?? []
    ),
    selfie: pickCurrentDocument(
      documentsByType[VerificationDocumentTypes.Selfie] ?? []
    ),
  };

  const partyWithTags = verificationDetails.value?.user?.partyWithTags ?? [];
  needSelfie.value = partyWithTags.includes("NeedSelfie");

  const currentDocumentKeys = new Set(
    Object.values(verificationDocuments.value)
      .filter(Boolean)
      .map(getDocumentKey)
  );

  // 历史文档: verification 类型中未被选为当前展示的旧版本
  historicalDocuments.value = documents
    .filter(
      (doc) =>
        isVerificationDocumentType(getDocumentType(doc)) &&
        !currentDocumentKeys.has(getDocumentKey(doc))
    )
    .sort((x, y) => getDocumentId(y) - getDocumentId(x));

  // 其他文档: 非 verification 类型
  otherDocuments.value = documents
    .filter((doc) => !isVerificationDocumentType(getDocumentType(doc)))
    .sort((x, y) => getDocumentId(y) - getDocumentId(x));
};

const fetchData = async () => {
  if (effectivePartyId.value == undefined || effectivePartyId.value == null) {
    verificationDetails.value = null;
    mediaList.value = [];
    resetDocuments();
    resetKyc();
    return;
  }

  isLoading.value = true;
  try {
    const userVerification = await UserService.queryVerifications({
      type: VerificationTypes.Verification,
      partyId: effectivePartyId.value,
    });

    verificationDetails.value = userVerification.data?.[0] ?? null;

    const mediaResult = await UserService.getClientMediaList({
      partyId: effectivePartyId.value,
    });

    mediaList.value = mediaResult.data ?? [];
    buildDocuments();
    await getKycForm();
  } catch (e) {
    console.log(e);
    resetDocuments();
    resetKyc();
  } finally {
    isLoading.value = false;
  }
};

const getKycForm = async () => {
  resetKyc();
  try {
    const res = await UserService.queryVerifications({
      type: VerificationTypes.KycForm,
      status: VerificationStatusTypes.Approved,
      partyId: effectivePartyId.value,
    });
    if (res.data.length != 0) {
      finalizeKyc.value = await UserService.getKycForm(effectivePartyId.value);
    }
  } catch (e) {
    console.log(e);
  }
};

const showFinalizedKyc = async () => {
  if (effectivePartyId.value == undefined || effectivePartyId.value == null) {
    return;
  }

  kycLoading.value = true;
  try {
    if (finalizeKyc.value["bg1"] == "" && finalizeKyc.value["bg2"] == "") {
      const userVerification = await UserService.queryVerifications({
        partyId: effectivePartyId.value,
      });
      const latestVerificationDetails = await UserService.getVerificationById(
        userVerification.data[0].id
      );

      finalizeKyc.value.financialInfo =
        latestVerificationDetails.financial ?? {};
      finalizedAuKycModal.value?.show(finalizeKyc.value);
      return;
    }
    finalizedKycModal.value?.show(finalizeKyc.value);
  } catch (e) {
    console.log(e);
  } finally {
    kycLoading.value = false;
  }
};

const refreshData = async () => {
  await fetchData();
  // emits("updateVerificationDetails");
};

provide(userDocumentsInjectionKeys.verificationDetails, verificationDetails);
provide(
  userDocumentsInjectionKeys.verificationDocuments,
  verificationDocuments
);
provide(userDocumentsInjectionKeys.historicalDocuments, historicalDocuments);
provide(userDocumentsInjectionKeys.otherDocuments, otherDocuments);
provide(userDocumentsInjectionKeys.canVerifyDocument, canVerifyDocument);
provide(userDocumentsInjectionKeys.finalizeKyc, finalizeKyc);
provide(userDocumentsInjectionKeys.kycLoading, kycLoading);
provide(userDocumentsInjectionKeys.mediaList, mediaList);
provide(userDocumentsInjectionKeys.needSelfie, needSelfie);
provide(userDocumentsInjectionKeys.isShowUserInfos, isShowUserInfos);
provide(userDocumentsInjectionKeys.isLoading, isLoading);
provide(userDocumentsInjectionKeys.refreshData, refreshData);
provide(userDocumentsInjectionKeys.showFinalizedKyc, showFinalizedKyc);

onMounted(async () => {
  await fetchData();
});

watch(
  () => effectivePartyId.value,
  async () => {
    await fetchData();
  }
);

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "overflow-auto",
      style: _normalizeStyle([{"flex":"1","overflow-y":"auto"}, {
        height: `${props.height ?? '100%'} !important`,
        'border-right': isShowUserInfos.value && `1px dashed #999`,
      }])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h1", _hoisted_3, [
          _createElementVNode("span", _hoisted_4, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("tip.verifyDocuments")), 1),
            _createVNode(_component_el_button, {
              type: "primary",
              plain: "",
              disabled: !verificationDetails.value?.id,
              onClick: openDocumentFileUploadModal
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_icon, null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DocumentAdd))
                  ]),
                  _: 1
                }),
                _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t("action.upload")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            (
                verificationDetails.value?.status !== _unref(VerificationStatusTypes).Approved
              )
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (!isShowUserInfos.value)
                    ? (_openBlock(), _createBlock(_component_el_button, {
                        key: 0,
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (isShowUserInfos.value = true))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("action.showUserInfo")), 1),
                          _createVNode(_component_el_icon, null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(ArrowRightBold))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (isShowUserInfos.value)
                    ? (_openBlock(), _createBlock(_component_el_button, {
                        key: 1,
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (isShowUserInfos.value = false))
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_8, _toDisplayString(_ctx.$t("action.hideUserInfo")), 1),
                          _createVNode(_component_el_icon, null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(ArrowLeftBold))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ], 64))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      (isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _createVNode(ScaleLoader, { color: '#ffc730' })
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createVNode(VerificationDocuments),
            _createVNode(OtherDocuments),
            _createVNode(HistoricalDocuments),
            _createVNode(KycDocuments)
          ], 64))
    ], 4),
    (isShowUserInfos.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createVNode(UserPersonalInfos, { "verification-details": verificationDetails.value }, null, 8, ["verification-details"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(DocumentFileUploadModal, {
      ref_key: "documentFileUploadModal",
      ref: documentFileUploadModal,
      onRefresh: refreshData
    }, null, 512),
    _createVNode(FinalizedKycModal, {
      ref_key: "finalizedKycModal",
      ref: finalizedKycModal
    }, null, 512),
    _createVNode(FinalizedAuKycModal, {
      ref_key: "finalizedAuKycModal",
      ref: finalizedAuKycModal
    }, null, 512)
  ]))
}
}

})